import React, { useState, useEffect } from 'react';
import Dialog from 'src/components/common/Dialog.redesign';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import Textbox from 'src/components/controls/forms/redesign/FormTextbox';
import { SelectItem } from 'src/models/common/SelectItem';
import TagContainer from 'src/components/counties/TagContainer';
import Button from 'src/components/controls/forms/redesign/FormButton';
import useFetch from 'use-http';
import { Url } from 'src/api';
import { useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import County from 'src/models/County';
import Tag from 'src/models/Tag';
import { FormErrors } from 'src/util/FormErrors';
import Section from 'src/models/Section';
import { LandSystem } from 'src/models/LandSystem';
import { AnyObject, FormApi } from 'final-form';
import { FieldArray } from 'react-final-form-arrays';
import FormSelect from 'src/components/controls/forms/redesign/FormFilterSelect';
import useDebounce from 'src/util/hooks/use-debounce';
// import Abstract from 'src/models/Abstract';

// type AbstractSearch = Abstract & { loading: boolean };

interface ISectionDialogProps {
    onCloseDialog?: () => void;
    action: 'create' | 'edit' | 'delete';
    townshipId: number;
    sectionNumber: number;
    onUpdateComplete: (sectionNumber: number) => void;
    landSystem: LandSystem;
}

const SectionDialog: React.FC<ISectionDialogProps> = (props) => {
    const appContext = useSelector((storeState: IRootState) => storeState.appContext);
    const { get, response, post, put } = useFetch();
    const [currentSection, setCurrentSection] = useState(undefined);
    const [counties, setCounties] = useState([]);
    const [tags, setTags] = useState([]);
    const [searchParams, setSearchParams] = useState(undefined);
    const debouncedAbstractSearch = useDebounce(searchParams, 500);

    const searchAbstract = async (countyId: number, abstractNumber: string, index: number, formApi: FormApi) => {
        const result = await get(`${Url.project.getAbstract(appContext, countyId, abstractNumber)}`);

        if (response.ok) {
            const wtCounty = (formApi.getState().values as AnyObject).westTexasCounties[index];

            formApi.change(`westTexasCounties[${index}].loading`, false);
            if (parseInt(wtCounty.countyId, 10) === countyId && `${wtCounty.abstractNumber}` === abstractNumber) {
                formApi.change(`westTexasCounties[${index}].abstractId`, result.id);
                formApi.change(`westTexasCounties[${index}].name`, result.name);
                formApi.change(`westTexasCounties[${index}].acres`, result.acres);
                formApi.change(`westTexasCounties[${index}].legal`, result.legal);
                formApi.change(`westTexasCounties[${index}].section`, result.section);
            }
        }
    };

    useEffect(() => {
        if (debouncedAbstractSearch) {
            const { formApi, index, countyId, abstractNumber } = debouncedAbstractSearch as AnyObject;
            searchAbstract(countyId, abstractNumber, index, formApi);
        }
    }, [debouncedAbstractSearch]);

    const dialogTitle = () => {
        switch (props.action) {
            case 'create':
                return 'Create Section';
                break;
            case 'edit':
                return 'Edit Section';
                break;
            case 'delete':
                return 'Delete Section';
                break;
            default:
                return '[Error]';
                break;
        }
    };

    const buttonTitle = () => {
        switch (props.action) {
            case 'create':
                return 'Create Section';
                break;
            case 'edit':
                return 'Save Changes';
                break;
            case 'delete':
                return 'Delete Section';
                break;
            default:
                return '[Error]';
                break;
        }
    };

    const loadData = async () => {
        if (props.action === 'edit') {
            const data = await get(Url.project.getSection(appContext, props.sectionNumber));

            if (response.ok) {
                if (data.sectionNumber === 0) {
                    data.sectionNumber = undefined;
                }
                setCurrentSection(data);
            }
        }

        const counitesCall = get(Url.project.getTownshipCounties(appContext, props.townshipId));
        const tagsCall = get(Url.project.getProjectTags(appContext));

        const [countiesData, tagsData] = [await counitesCall, await tagsCall];

        if (response.ok) {
            setCounties(countiesData.items);
            setTags(tagsData);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const submitForm = async (values: AnyObject) => {
        if (props.action === 'create') {
            if (props.landSystem === LandSystem.WestTexas) {
                if (values.sectionNumber) {
                    values.sectionNumber = parseInt(values.sectionNumber + '', 10);
                }

                values.westTexasCounties = values.westTexasCounties
                    .filter((wtc: AnyObject) => wtc.countyId !== 0)
                    .map((wtc: AnyObject) => {
                        if (wtc.abstractNumber) {
                            wtc.abstractNumber = wtc.abstractNumber;
                        }
                        return wtc;
                    });

                const result = await post(Url.project.getSectionsByTownship(appContext, props.townshipId), values);

                if (response.ok) {
                    props.onUpdateComplete(result.id);
                    props.onCloseDialog();
                }
            } else {
                if (values.sectionNumber) {
                    values.sectionNumber = parseInt(values.sectionNumber + '', 10);
                }

                const result = await post(Url.project.getSectionsByTownship(appContext, props.townshipId), values);

                if (response.ok) {
                    props.onUpdateComplete(result.id);
                    props.onCloseDialog();
                }
            }
        }

        if (props.action === 'edit') {
            if (props.landSystem === LandSystem.WestTexas) {
                if (values.sectionNumber) {
                    values.sectionNumber = parseInt(values.sectionNumber + '', 10);
                }

                values.westTexasCounties = values.westTexasCounties
                    .filter((wtc: AnyObject) => wtc.countyId !== 0)
                    .map((wtc: AnyObject) => {
                        if (wtc.abstractNumber) {
                            wtc.abstractNumber = wtc.abstractNumber;
                        }
                        return wtc;
                    });

                await put(Url.project.getSection(appContext, props.sectionNumber), values);

                if (response.ok) {
                    props.onUpdateComplete(props.sectionNumber);
                    props.onCloseDialog();
                }
            } else {
                values.sectionNumber = parseInt(values.sectionNumber + '', 10);
                await put(Url.project.getSection(appContext, props.sectionNumber), values);

                if (response.ok) {
                    props.onUpdateComplete(props.sectionNumber);
                    props.onCloseDialog();
                }
            }
        }
    };

    const jeffersonianSection = (formApi: FormApi) => {
        return (
            <>
                <Textbox label="Section" name="sectionNumber" />
                <TagContainer
                    label="Counties"
                    fieldArrayName="counties"
                    fieldAddArrayName="addCounties"
                    fieldDeleteArrayName="deleteCounties"
                    initialValueName="county"
                    isTagCreatable={false}
                    form={formApi}
                    selectTags={counties.length > 0 ? counties.map((c: County) => new SelectItem(c.id, c.name)) : new Array<SelectItem>()}
                />
                <TagContainer
                    label="Tags"
                    fieldArrayName="tags"
                    fieldAddArrayName="addTags"
                    fieldDeleteArrayName="deleteTags"
                    initialValueName="tag"
                    form={formApi}
                    isTagCreatable={true}
                    selectTags={tags.length > 0 ? tags.map((t: Tag) => new SelectItem(t.id, t.name)) : new Array<SelectItem>()}
                />
            </>
        );
        return <h2>Jeffersonian Section</h2>;
    };

    const abstractPanel = (value: AnyObject) => {
        let content = undefined;

        if (value.loading) {
            content = <div>Loading...</div>;
        } else if (!value.id && (!value.countyId || !value.abstractNumber)) {
            content = <div>Enter County and Abstract Number to Find Abstract</div>;
        } else if (!value.abstractId && value.countyId && value.abstractNumber) {
            content = <div>Abstract Not Found</div>;
        } else {
            content = (
                <div>
                    <div>
                        <span style={{ fontWeight: 'bold' }}>Name:</span> {value.name}
                        <span style={{ fontWeight: 'bold' }}>&nbsp;&nbsp;&nbsp;Acres:</span> {value.acres}
                    </div>
                    <div>
                        <span style={{ fontWeight: 'bold' }}>Legal:</span> {value.legal || '[No Legal]'}
                        <span style={{ fontWeight: 'bold' }}>&nbsp;&nbsp;&nbsp;Section:</span> {value.section || '[No Section]'}
                    </div>
                </div>
            );
        }

        return <div style={{ width: '100%', padding: '1rem', border: '1px solid #ccc', background: 'white', marginBottom: '1rem' }}>{content}</div>;
    };

    const handleFormChange = (formApi: FormApi, field: string, index: number) => {
        const wtCounty = (formApi.getState().values as AnyObject)?.westTexasCounties[index];
        formApi.change(`westTexasCounties[${index}].abstractId`, 0);
        if (wtCounty && wtCounty.countyId && wtCounty.abstractNumber) {
            formApi.change(`westTexasCounties[${index}].loading`, true);
            if (field === 'countyId') {
                searchAbstract(parseInt(wtCounty.countyId, 10), wtCounty.abstractNumber, index, formApi);
            } else {
                setSearchParams({ countyId: parseInt(wtCounty.countyId, 10), abstractNumber: wtCounty.abstractNumber, index, formApi });
            }
        }
    };

    const westTexasSection = (formApi: FormApi, errors: AnyObject) => {
        return (
            <>
                <Textbox label="Section" name="sectionNumber" />
                <FieldArray name="westTexasCounties">
                    {({ fields }) => (
                        <>
                            <fieldset
                                style={{
                                    border: '1px solid #d5d5d5',
                                    borderLeftWidth: '0',
                                    borderRightWidth: '0',
                                    marginBottom: '.5rem',
                                    padding: '1rem 0',
                                }}
                            >
                                <legend>Abstracts</legend>
                                {formApi.getState().submitFailed && errors.countyError ? (
                                    <div
                                        style={{
                                            padding: '.5rem',
                                            marginBottom: '.5rem',
                                            border: '2px solid red',
                                            background: '#FFF7D9',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {errors.countyError}
                                    </div>
                                ) : undefined}
                                {fields.map((name: string, index: number) => (
                                    <div key={name}>
                                        <div style={{ display: 'flex' }}>
                                            <FormSelect
                                                name={`${name}.countyId`}
                                                // isDisabled={loadingAbstract}
                                                items={counties}
                                                label="County"
                                                onChange={() => handleFormChange(formApi, 'countyId', index)}
                                            />
                                            <Textbox
                                                name={`${name}.abstractNumber`}
                                                label="Abstract Number"
                                                style={{ marginLeft: '1rem' }}
                                                onChange={() => handleFormChange(formApi, 'abstractNumber', index)}
                                                // isDisabled={loadingAbstract}
                                            />
                                            <div
                                                style={{ position: 'relative' }}
                                                onClick={() => {
                                                    fields.remove(index);
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        marginLeft: '.5rem',
                                                        padding: '.5rem',
                                                        color: 'red',
                                                        position: 'absolute',
                                                        bottom: '2.6rem',
                                                        border: '1px solid #ccc',
                                                        background: 'white',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <i className="fa fa-times" />
                                                </span>
                                            </div>
                                        </div>
                                        <div>{abstractPanel(fields.value[index])}</div>
                                    </div>
                                ))}
                                <Button
                                    text="Add Abstract"
                                    onClick={() => {
                                        const as = { countyId: 0, abstractNumber: '', loading: false, id: 0 };
                                        fields.push(as);
                                    }}
                                />
                            </fieldset>
                        </>
                    )}
                </FieldArray>
                <TagContainer
                    label="Tags"
                    fieldArrayName="tags"
                    fieldAddArrayName="addTags"
                    fieldDeleteArrayName="deleteTags"
                    initialValueName="tag"
                    form={formApi}
                    isTagCreatable={true}
                    selectTags={tags.length > 0 ? tags.map((t: Tag) => new SelectItem(t.id, t.name)) : new Array<SelectItem>()}
                />
            </>
        );
    };

    const validateForm = (section: Section) => {
        const errors: FormErrors<Section & { countyError: string }> = {};

        if (props.landSystem === LandSystem.Jeffersonian) {
            section.sectionNumber = parseInt(section.sectionNumber + '', 10);
            const sectionNumberRegex = /^[1-9]\d{0,1}$/;

            if (!section.sectionNumber && !section.abstractId) {
                errors.sectionNumber = 'Valid Section Number Required';
            } else {
                if (!section.sectionNumber) {
                    errors.sectionNumber = 'Valid Section Number Required';
                }
                if (!sectionNumberRegex.test(section.sectionNumber.toString())) {
                    errors.sectionNumber = 'Please enter a valid section number';
                }

                if (Number.isNaN(section.sectionNumber)) {
                    errors.sectionNumber = 'Must be Number';
                }
            }
        } else {
            if (!(section.westTexasCounties && section.westTexasCounties.length > 0 && section.westTexasCounties[0].countyId)) {
                errors.countyError = 'At least one abstract needs to be added';
            }

            if (Number.isNaN(section.sectionNumber)) {
                errors.sectionNumber = 'Must be Number';
            }
        }

        return errors;
    };

    return (
        <Dialog title={dialogTitle()}>
            <Form
                onSubmit={submitForm}
                validate={validateForm}
                initialValues={currentSection}
                mutators={{ ...arrayMutators }}
                render={({ handleSubmit, form, errors }) => (
                    <form onSubmit={handleSubmit} className="dlc-form">
                        {props.landSystem === LandSystem.Jeffersonian ? jeffersonianSection(form) : westTexasSection(form, errors)}
                        <Button type="submit" text={buttonTitle()} />
                    </form>
                )}
            />
        </Dialog>
    );
};

export default SectionDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/dialogs/SectionDialog.tsx